import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface BinaryPuzzle {
  grid: (number | null)[][];
  solution: number[][];
  seed: number;
  size: number;
}

export default function BinaryGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;

  const [puzzle, setPuzzle] = useState<BinaryPuzzle | null>(null);
  const [boardState, setBoardState] = useState<(number | null)[][]>([]);
  const [originalGrid, setOriginalGrid] = useState<(number | null)[][]>([]);
  const [history, setHistory] = useState<(number | null)[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Iniciando generación...');
  const [error, setError] = useState('');

  const dificultadOrden = parseInt(dificultad || '1');
  const isFirstLoad = useRef(true);
  const [forceReload, setForceReload] = useState(0);
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 20);
      setBoardH(clientHeight - 20);
    }
  };

  // Medir tras un frame para que el DOM esté asentado
  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);
  const { saveProgress: saveGameProgress } = useGameProgress('binary', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Mapeo de dificultad a tamaño
  const getSizeFromDificultad = (orden: number): number => {
    const sizeMap: Record<number, number> = {
      1: 6,   // Neurona
      2: 8,   // Sinapsis
      3: 10,  // Cerebro
      4: 12,  // Genio
      5: 14,  // Einstein
    };
    return sizeMap[orden] || 6;
  };

  // Cargar puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingMessage('Iniciando generación...');
        setError('');

        // Simular progreso mientras se genera
        const progressInterval = setInterval(() => {
          setLoadingProgress(prev => {
            if (prev < 90) {
              const increment = Math.random() * 15;
              const newProgress = Math.min(prev + increment, 90);

              // Mensajes según progreso
              if (newProgress < 30) {
                setLoadingMessage('Generando solución...');
              } else if (newProgress < 60) {
                setLoadingMessage('Optimizando dificultad...');
              } else {
                setLoadingMessage('Finalizando puzzle...');
              }

              return newProgress;
            }
            return prev;
          });
        }, 200);

        const size = getSizeFromDificultad(dificultadOrden);
        const isNewGame = forceReload > 0;
        const response = await api.get('/binary/generate', {
          params: { size, ...(!isNewGame && seedParam !== undefined && { seed: seedParam }) }
        });

        clearInterval(progressInterval);
        setLoadingProgress(100);
        setLoadingMessage('¡Listo!');

        const puzzleData: BinaryPuzzle = response.data.data;
        setPuzzle(puzzleData);

        // Actualizar URL con seed
        const newParams = new URLSearchParams(searchParams);
        newParams.set('seed', puzzleData.seed.toString());
        setSearchParams(newParams, { replace: true });

        // Copiar grid original
        const original = puzzleData.grid.map(row => [...row]);
        setOriginalGrid(original);

        // Inicializar estado del tablero
        const initialState = puzzleData.grid.map(row => [...row]);
        setBoardState(initialState);
        setHistory([JSON.parse(JSON.stringify(initialState))]);
        setHistoryIndex(0);
        setMoves(0);
        setIsCompleted(false);
        setShowVictory(false);

        // Pequeño delay para mostrar el 100%
        setTimeout(() => {
          setLoading(false);
        }, 300);
      } catch (err: any) {
        console.error('Error al cargar puzzle:', err);
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
        setLoading(false);
      } finally {
        isFirstLoad.current = false;
      }
    };

    loadPuzzle();
  }, [dificultadOrden, forceReload]);

  // Guardar estado en historial
  const saveState = (newState: (number | null)[][]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newState)));

    // Limitar historial a 50 estados
    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // Toggle celda: null -> 0 -> 1 -> null
  const handleCellClick = (r: number, c: number) => {
    if (isCompleted || !puzzle || originalGrid[r][c] !== null) return;

    const newState = boardState.map(row => [...row]);
    const currentVal = newState[r][c];

    if (currentVal === null) newState[r][c] = 0;
    else if (currentVal === 0) newState[r][c] = 1;
    else newState[r][c] = null;

    setBoardState(newState);
    setMoves(prev => prev + 1);
    saveState(newState);

    // Verificar victoria automáticamente
    checkVictory(newState, false);
  };

  // Deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setBoardState(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  // Rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setBoardState(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  // Reiniciar
  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres reiniciar el tablero?')) return;

    const resetState = originalGrid.map(row => [...row]);
    setBoardState(resetState);
    setHistory([JSON.parse(JSON.stringify(resetState))]);
    setHistoryIndex(0);
    setMoves(0);
    setIsCompleted(false);
    setShowVictory(false);
    setError('');
  };

  // Ver solución
  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres ver la solución? Esto no guardará tu progreso.')) return;

    const solutionState = puzzle.solution.map(row => [...row]);
    setBoardState(solutionState);
    saveState(solutionState);
    setError('Solución revelada. No se guardará este progreso.');
  };

  // Dar pista
  const handleHint = () => {
    if (!puzzle) return;

    // Buscar errores primero
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (boardState[r][c] !== null && originalGrid[r][c] === null) {
          if (boardState[r][c] !== puzzle.solution[r][c]) {
            const newState = boardState.map(row => [...row]);
            newState[r][c] = puzzle.solution[r][c];
            setBoardState(newState);
            saveState(newState);
            setError('Error corregido');
            return;
          }
        }
      }
    }

    // Revelar celda vacía aleatoria
    const emptyCells: [number, number][] = [];
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (boardState[r][c] === null) {
          emptyCells.push([r, c]);
        }
      }
    }

    if (emptyCells.length === 0) {
      setError('¡Ya está completo!');
      return;
    }

    const [r, c] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const newState = boardState.map(row => [...row]);
    newState[r][c] = puzzle.solution[r][c];
    setBoardState(newState);
    setMoves(prev => prev + 1);
    saveState(newState);
    setError('');
    checkVictory(newState, false);
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(state: (number | null)[][], sz: number): Set<string> {
    const errores = new Set<string>();
    const half = sz / 2;

    // 3 consecutivos iguales horizontal
    for (let r = 0; r < sz; r++) {
      for (let c = 0; c <= sz - 3; c++) {
        const v = state[r][c];
        if (v !== null && v === state[r][c + 1] && v === state[r][c + 2]) {
          errores.add(`${r},${c}`); errores.add(`${r},${c + 1}`); errores.add(`${r},${c + 2}`);
        }
      }
    }

    // 3 consecutivos iguales vertical
    for (let c = 0; c < sz; c++) {
      for (let r = 0; r <= sz - 3; r++) {
        const v = state[r][c];
        if (v !== null && v === state[r + 1][c] && v === state[r + 2][c]) {
          errores.add(`${r},${c}`); errores.add(`${r + 1},${c}`); errores.add(`${r + 2},${c}`);
        }
      }
    }

    // Exceso de 0s o 1s en filas
    for (let r = 0; r < sz; r++) {
      let zeros = 0, ones = 0;
      for (let c = 0; c < sz; c++) {
        if (state[r][c] === 0) zeros++;
        else if (state[r][c] === 1) ones++;
      }
      if (zeros > half) for (let c = 0; c < sz; c++) { if (state[r][c] === 0) errores.add(`${r},${c}`); }
      if (ones > half)  for (let c = 0; c < sz; c++) { if (state[r][c] === 1) errores.add(`${r},${c}`); }
    }

    // Exceso de 0s o 1s en columnas
    for (let c = 0; c < sz; c++) {
      let zeros = 0, ones = 0;
      for (let r = 0; r < sz; r++) {
        if (state[r][c] === 0) zeros++;
        else if (state[r][c] === 1) ones++;
      }
      if (zeros > half) for (let r = 0; r < sz; r++) { if (state[r][c] === 0) errores.add(`${r},${c}`); }
      if (ones > half)  for (let r = 0; r < sz; r++) { if (state[r][c] === 1) errores.add(`${r},${c}`); }
    }

    // Filas completas duplicadas
    const rowMap = new Map<string, number>();
    for (let r = 0; r < sz; r++) {
      if (state[r].some(v => v === null)) continue;
      const key = state[r].join('');
      if (rowMap.has(key)) {
        const prev = rowMap.get(key)!;
        for (let c = 0; c < sz; c++) { errores.add(`${r},${c}`); errores.add(`${prev},${c}`); }
      } else rowMap.set(key, r);
    }

    // Columnas completas duplicadas
    const colMap = new Map<string, number>();
    for (let c = 0; c < sz; c++) {
      const col: (number | null)[] = [];
      for (let r = 0; r < sz; r++) col.push(state[r][c]);
      if (col.some(v => v === null)) continue;
      const key = col.join('');
      if (colMap.has(key)) {
        const prev = colMap.get(key)!;
        for (let r = 0; r < sz; r++) { errores.add(`${r},${c}`); errores.add(`${r},${prev}`); }
      } else colMap.set(key, c);
    }

    return errores;
  }

  // Validar que un tablero completo cumple todas las reglas del Binary
  const isValidBinaryBoard = (state: (number | null)[][], size: number): boolean => {
    const half = size / 2;
    const rowStrings: string[] = [];

    for (let r = 0; r < size; r++) {
      let zeros = 0, ones = 0, rowStr = '';
      for (let c = 0; c < size; c++) {
        const val = state[r][c];
        if (val === null) return false;
        if (val === 0) zeros++; else ones++;
        rowStr += val;
        if (c >= 2 && val === state[r][c - 1] && val === state[r][c - 2]) return false;
      }
      if (zeros !== half || ones !== half) return false;
      if (rowStrings.includes(rowStr)) return false;
      rowStrings.push(rowStr);
    }

    const colStrings: string[] = [];
    for (let c = 0; c < size; c++) {
      let zeros = 0, ones = 0, colStr = '';
      for (let r = 0; r < size; r++) {
        const val = state[r][c];
        if (val === 0) zeros++; else ones++;
        colStr += val;
        if (r >= 2 && val === state[r - 1][c] && val === state[r - 2][c]) return false;
      }
      if (zeros !== half || ones !== half) return false;
      if (colStrings.includes(colStr)) return false;
      colStrings.push(colStr);
    }

    return true;
  };

  // Verificar victoria
  const checkVictory = (state: (number | null)[][], manual: boolean) => {
    if (!puzzle) return;

    // Verificar si está completo
    let isFull = true;
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (state[r][c] === null) {
          isFull = false;
          break;
        }
      }
      if (!isFull) break;
    }

    if (!isFull) {
      if (manual) setError('El tablero aún no está completo');
      return;
    }

    // Validar contra las reglas del juego (no contra la solución almacenada)
    const isValid = isValidBinaryBoard(state, puzzle.size);

    if (isValid && !isCompleted) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);

      // Guardar progreso
      saveProgress(tiempo, moves);

      // Mostrar modal de victoria
      setShowVictory(true);
    } else if (manual && !isValid) {
      setError('Hay errores en el tablero');
    }
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number, movimientos: number) => {
    if (!puzzle) return;

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: puzzle.seed,
        tiempo,
        movimientos,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos,
        completado: true,
        datos_juego: {
          tablero_id: puzzle.seed,
          size: puzzle.size,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  const size = puzzle?.size ?? 6;
  const errores = modoAsistido && puzzle ? calcularErrores(boardState, size) : new Set<string>();
  const gridOverhead = (size - 1) * 2 + 4;
  const cellW = boardW > 0 ? Math.floor((boardW - gridOverhead) / size) : 60;
  const cellH = boardH > 0 ? Math.floor((boardH - gridOverhead) / size) : 60;
  const fontSize = `${Math.floor(Math.min(cellW, cellH) * 0.45)}px`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4 animate-pulse">💾</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Generando Binary Puzzle</h2>
            <p className="text-gray-600">{loadingMessage}</p>
          </div>

          {/* Barra de progreso */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-500 to-blue-500 h-4 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>

          <div className="text-center text-gray-500 text-sm">
            {Math.round(loadingProgress)}%
          </div>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">💾 Binary</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/binary/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => setForceReload(prev => prev + 1)} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"/></svg>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board area - fills remaining space */}
      <div ref={boardAreaRef} className="flex-1 flex items-center justify-center p-2 min-h-0">
        <div
          className="binary-board inline-grid gap-0.5 bg-gray-800 p-0.5 rounded-lg"
          style={{
            gridTemplateColumns: `repeat(${puzzle.size}, ${cellW}px)`,
            gridTemplateRows: `repeat(${puzzle.size}, ${cellH}px)`,
          }}
        >
          {Array.from({ length: puzzle.size }, (_, r) =>
            Array.from({ length: puzzle.size }, (_, c) => {
              const val = boardState[r][c];
              const isLocked = originalGrid[r][c] !== null;
              const hasError = errores.has(`${r},${c}`);

              // Colores via inline styles (Tailwind purga clases dinámicas en producción)
              // Sistema: mismos colores (rojo/verde) pero borde ámbar grueso para distinguir
              let bgColor: string;
              const textColor = '#ffffff';
              let border = 'none';

              if (hasError) {
                bgColor = '#f97316';
              } else if (val === 0) {
                bgColor = '#ef4444';
              } else if (val === 1) {
                bgColor = '#22c55e';
              } else {
                bgColor = '#d1d5db';
              }

              if (isLocked && !hasError) {
                border = '4px solid #b45309'; // borde ámbar grueso = puesto por el sistema
              }

              return (
                <div
                  key={`${r}-${c}`}
                  onClick={() => handleCellClick(r, c)}
                  className="rounded transition-all flex items-center justify-center font-bold"
                  style={{
                    width: `${cellW}px`,
                    height: `${cellH}px`,
                    fontSize: fontSize,
                    backgroundColor: bgColor,
                    color: textColor,
                    border: border,
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                  }}
                >
                  {val !== null ? val : ''}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal Cómo se juega */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Binary Puzzle"
        gameIcon="💾"
        objective="Completa el tablero con 0s y 1s sin romper las reglas del puzzle binario."
        rules={[
          'No puede haber más de dos números iguales consecutivos en ninguna dirección (ni horizontal ni vertical)',
          'Cada fila y columna debe tener exactamente la misma cantidad de 0s y 1s',
          'No puede haber dos filas idénticas',
          'No puede haber dos columnas idénticas',
        ]}
        howToPlay="Click en una celda vacía para alternar entre: Vacío → 0 (Rojo) → 1 (Verde) → Vacío"
        author="Adolfo Zanellati"
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
